import React, {useEffect, useState} from "react";
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import { ArrowBackIosNew } from "@mui/icons-material";
import { GridSaveAltIcon } from "@mui/x-data-grid";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button, Modal, Grid, Stack, Typography, ModalClose, Sheet } from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IAllocatedResource from "../../../types/allocated-resource.type";
import {Autocomplete} from "@mui/lab";
import IUser from "../../../types/user.type";
import {ServiceOrderStatus} from "../../../types/enumeration/ServiceOrderStatus";
import {ServiceOrderStatusNames} from "../../../types/enumeration/ServiceOrderStatusNames";
import {WorkPositionNames} from "../../../types/enumeration/WorkPositionNames";
import {WorkPosition} from "../../../types/enumeration/WorkPosition";

interface ModalProps {
  isOpen: boolean,
  onSubmit: (data: IAllocatedResource) => void,
  onClose: () => void,
  allocatedResource: IAllocatedResource | undefined,
  users: IUser[],
  readOnly?: boolean;
}

const AllocatedResourceModal: React.FC<ModalProps> = (props) => {
  const { isOpen, onClose, onSubmit, allocatedResource, users, readOnly = false} = props;

  const initialValues: IAllocatedResource = {
    id: allocatedResource?.id,
    workPosition: WorkPosition.FIELD_OPERATOR,
    worker: null,
    confirmed: false
  };

  const validationSchema = Yup.object({
    worker: Yup.object().required('El Recurso es requerido'),
    workPosition: Yup.string().required('La Posicion es requerida'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
  });

  const handleChangeWorkPosition = (event: SelectChangeEvent) => {
    formik.setFieldValue('workPosition', event.target.value)
  };

  useEffect(() => {
    if (isOpen && allocatedResource?.id) {
      formik.setValues(allocatedResource);
    } else {
      formik.resetForm()
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Sheet
        variant="outlined"
        sx={{
          width: 730,
          borderRadius: 'md',
          p: 3,
          boxShadow: 'lg',
          maxHeight: 700,
          // overflow: "scroll"
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} />
        <Typography level="h4" component="h1" sx={{ paddingLeft: 1 }}>
          {allocatedResource?.id ? "Editar Recurso Asigado" : "Nuevo Recurso Asignado"}
        </Typography>
        <form className="form" onSubmit={formik.handleSubmit}>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <Autocomplete
                  id="workerId"
                  value={formik.values.worker}
                  getOptionLabel={(user) =>
                    `${user.lastName || ''}, ${user?.name || ''}`
                  }
                  options={users}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id;
                  }}
                  onChange={(e, value) => {
                    formik.setFieldValue('worker', value ? value : null);
                  }}
                  fullWidth
                  disabled={allocatedResource?.id}
                  readOnly={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant={"filled"}
                      label="Recurso Asigado"
                      slotProps={{ inputLabel: {shrink: true}}}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={!!(formik.touched.worker && formik.errors.worker)}
                    />
                  )}
                />
                {formik.touched.worker && formik.errors.worker ? (
                  <span className="Error-alert">{String(formik.errors.worker)}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="type-label" shrink>Posicion</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    label="type"
                    value={formik.values.workPosition}
                    onBlur={formik.handleBlur}
                    onChange={handleChangeWorkPosition}
                    readOnly={readOnly}
                  >
                    {Object
                      .keys(WorkPosition)
                      .map((key) => (
                        <MenuItem key={key} value={WorkPosition[key as keyof typeof WorkPosition]}>
                          {WorkPositionNames[WorkPosition[key as keyof typeof WorkPosition]]}
                        </MenuItem>
                      ))}
                  </Select>
                  {formik.touched.workPosition && formik.errors.workPosition ? (
                    <span className="Error-alert">{formik.errors.workPosition}</span>
                  ) : null}
                </FormControl>
              </CssVarsProvider>
            </Grid>

          </Grid>
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{paddingRight: 1}}>
            <Button variant="outlined" endDecorator={<ArrowBackIosNew />} onClick={onClose}>Cerrar</Button>
            {!readOnly &&
              <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
            }
          </Stack>
        </form>
      </Sheet>
    </Modal>
  );
};

export default AllocatedResourceModal;
